import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Badge,
  Modal,
  Form,
  Alert,
  Spinner
} from 'react-bootstrap';
import { fetchCrmOpportunitiesById } from '../../../services/crmOpportunityService';
import { createCrmActivity } from '../../../services/crmActivityService';
import { createCrmCommunication } from '../../../services/crmCommService';
import { createCrmTask } from '../../../services/crmTaskService';
import { fetchUsers } from '../../../services/userService';

const activityTypes = ['Call', 'Email', 'Meeting', 'Follow-up'];
const communicationTypes = ['Email', 'Call', 'Meeting Note'];
const taskStatuses = ['Pending', 'In Progress', 'Completed'];
const priorities = ['High', 'Medium', 'Low'];

const OpportunityDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [opportunity, setOpportunity] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const oppData = await fetchCrmOpportunitiesById(id);
        const userData = await fetchUsers();
        setOpportunity(oppData);
        setUsers(userData);
      } catch (err) {
        setError('Failed to fetch opportunity details.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const navigate = useNavigate();

  const handleSubmit = async (
    type: string,
    data: any,
    setModalState: (state: boolean) => void
  ) => {
    setIsSubmitting(true);
    try {
      let response;
      if (type === 'activity') response = await createCrmActivity(data);
      else if (type === 'communication')
        response = await createCrmCommunication(data);
      else if (type === 'task') response = await createCrmTask(data);

      setMessage(response?.message || `${type} added successfully!`);
      const updatedOpportunity = await fetchCrmOpportunitiesById(id);
      setOpportunity(updatedOpportunity);
      setModalState(false);
    } catch (error) {
      setMessage(`Failed to add ${type}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return <Spinner animation="border" className="d-block mx-auto" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="primary"
          onClick={() => navigate(`/crm/opportunities/${id}/create-quote`)}
        >
          Create Quote
        </Button>
        <Button
          variant="primary"
          onClick={() => navigate(`/crm/opportunities/edit-opportunity/${id}`)}
        >
          Edit
        </Button>
      </div>

      {message && <Alert variant="info">{message}</Alert>}

      {/* 🔹 Opportunity Overview */}
      <Card className="shadow p-4">
        <Card.Body>
          <Card.Title className="fw-bold">{opportunity.natural_key}</Card.Title>
          <Badge bg="info" className="mb-2">
            {opportunity.status}
          </Badge>
          <p>
            <strong>Priority:</strong> {opportunity.priority}
          </p>
          <p>
            <strong>Source:</strong> {opportunity.source}
          </p>
          <p>
            <strong>Assigned To:</strong> {opportunity.user.username}
          </p>
          <p>
            <strong>Expected Close Date:</strong>{' '}
            {opportunity.expected_close_date || 'N/A'}
          </p>
          <p>
            <strong>Created At:</strong>{' '}
            {new Date(opportunity.created_at).toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      {/* 🔹 Action Buttons */}
      <div className="d-flex gap-3 mt-4">
        <Button variant="success" onClick={() => setShowActivityModal(true)}>
          ➕ Add Activity
        </Button>
        <Button
          variant="primary"
          onClick={() => setShowCommunicationModal(true)}
        >
          📞 Add Communication
        </Button>
        <Button variant="warning" onClick={() => setShowTaskModal(true)}>
          📝 Add Task
        </Button>
      </div>

      {/* 🔹 Activity Log */}
      <Card className="mt-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Activity Log</h5>
          {opportunity.activities?.length > 0 ? (
            opportunity.activities.map((activity: any) => (
              <div key={activity.activity_id} className="border-bottom py-2">
                <Badge bg="dark" className="me-2">
                  {activity.activity_type}
                </Badge>
                <p>{activity.description}</p>
              </div>
            ))
          ) : (
            <p className="text-muted">No Activities Found</p>
          )}
        </Card.Body>
      </Card>

      {/* 🔹 Communication Log */}
      <Card className="mt-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Communication Log</h5>
          {opportunity.communications?.length > 0 ? (
            opportunity.communications.map((comm: any) => (
              <div key={comm.communication_id} className="border-bottom py-2">
                <Badge bg="primary" className="me-2">
                  {comm.communication_type}
                </Badge>
                <p>{comm.details}</p>
              </div>
            ))
          ) : (
            <p className="text-muted">No Communications Found</p>
          )}
        </Card.Body>
      </Card>

      {/* 🔹 Task Log */}
      <Card className="mt-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Tasks</h5>
          {opportunity.tasks?.length > 0 ? (
            opportunity.tasks.map((task: any) => (
              <div key={task.task_id} className="border-bottom py-2">
                <Badge bg="warning" className="me-2">
                  {task.status}
                </Badge>
                <p>Description : {task.task_description}</p>
                <p>Priority: {task.priority}</p>
                <p>Due Date: {task.due_date}</p>
              </div>
            ))
          ) : (
            <p className="text-muted">No Tasks Found</p>
          )}
        </Card.Body>
      </Card>

      {/* 🔹 Modals */}
      {/* Activity Modal */}
      <Modal
        show={showActivityModal}
        onHide={() => setShowActivityModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Activity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Activity Type</Form.Label>
            <Form.Select id="activity_type">
              <option value="">Select Type</option>
              {activityTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Activity Date</Form.Label>
            <Form.Control id="activity_date" type="datetime-local" />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Description</Form.Label>
            <Form.Control id="activity_description" as="textarea" rows={3} />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Assigned To</Form.Label>
            <Form.Select id="activity_assigned">
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.username}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              const data = {
                related_to_id: id,
                related_to_type: 'Opportunity',
                activity_type: (
                  document.getElementById('activity_type') as HTMLSelectElement
                ).value,
                activity_date: (
                  document.getElementById('activity_date') as HTMLInputElement
                ).value,
                description: (
                  document.getElementById(
                    'activity_description'
                  ) as HTMLTextAreaElement
                ).value,
                assigned_to: (
                  document.getElementById(
                    'activity_assigned'
                  ) as HTMLSelectElement
                ).value
              };
              handleSubmit('activity', data, setShowActivityModal);
            }}
          >
            Save Activity
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Communication Modal */}
      <Modal
        show={showCommunicationModal}
        onHide={() => setShowCommunicationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Communication</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Communication Type</Form.Label>
            <Form.Select id="communication_type">
              <option value="">Select Type</option>
              {communicationTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Details</Form.Label>
            <Form.Control id="communication_details" as="textarea" rows={3} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              const data = {
                related_to_id: id,
                related_to_type: 'Opportunity',
                communication_type: (
                  document.getElementById(
                    'communication_type'
                  ) as HTMLSelectElement
                ).value,
                details: (
                  document.getElementById(
                    'communication_details'
                  ) as HTMLTextAreaElement
                ).value
              };
              handleSubmit('communication', data, setShowCommunicationModal);
            }}
          >
            Save Communication
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🔹 Task Modal */}
      <Modal
        show={showTaskModal}
        onHide={() => setShowTaskModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Task Name</Form.Label>
            <Form.Control
              id="task_name"
              type="text"
              placeholder="Enter Task Name"
              required
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              id="task_description"
              as="textarea"
              rows={3}
              required
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Status</Form.Label>
            <Form.Select id="task_status">
              <option value="">Select Status</option>
              {taskStatuses.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Priority</Form.Label>
            <Form.Select id="task_priority">
              <option value="">Select Priority</option>
              {priorities.map(priority => (
                <option key={priority} value={priority}>
                  {priority}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Due Date</Form.Label>
            <Form.Control id="task_due_date" type="date" required />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Assigned To</Form.Label>
            <Form.Select id="task_assigned">
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.username}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              const data = {
                related_to_id: id,
                related_to_type: 'Opportunity',
                task_name: (
                  document.getElementById('task_name') as HTMLInputElement
                ).value,
                task_description: (
                  document.getElementById(
                    'task_description'
                  ) as HTMLTextAreaElement
                ).value,
                status: (
                  document.getElementById('task_status') as HTMLSelectElement
                ).value,
                priority: (
                  document.getElementById('task_priority') as HTMLSelectElement
                ).value,
                due_date: (
                  document.getElementById('task_due_date') as HTMLInputElement
                ).value,
                assigned_to: (
                  document.getElementById('task_assigned') as HTMLSelectElement
                ).value
              };
              handleSubmit('task', data, setShowTaskModal);
            }}
          >
            Save Task
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OpportunityDetails;
